import { useState, useEffect, useCallback } from 'react';

const getApiUrl = () => {
  if (!process.env.REACT_APP_API_URL) {
    throw new Error('REACT_APP_API_URL environment variable is required');
  }
  return process.env.REACT_APP_API_URL;
};

interface EmbedFilters {
  team?: string;
  horizontalTeam?: string;
  month?: string;
  employee?: string;
  refresh?: number;
}

function buildQueryParams(filters: EmbedFilters): string {
  const params = new URLSearchParams();
  if (filters.month) params.append('month', filters.month);
  if (filters.team) params.append('team', filters.team);
  if (filters.horizontalTeam) params.append('horizontalTeam', filters.horizontalTeam);
  if (filters.employee) params.append('employee', filters.employee);
  return params.toString();
}

function useAutoRefresh(fetchFn: () => void, refreshInterval: number) {
  useEffect(() => {
    if (refreshInterval <= 0) return;
    const interval = setInterval(fetchFn, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchFn, refreshInterval]);
}

export function useKPIData(filters: EmbedFilters) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { team, horizontalTeam, month, employee } = filters;
  const fetchData = useCallback(async () => {
    try {
      const qs = buildQueryParams({ team, horizontalTeam, month, employee });
      const response = await fetch(`${getApiUrl()}/api/kpi?${qs}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const result = await response.json();
      setData({
        budgetTotalMonth: result.budgetTotalMonth ?? 0,
        budgetAsOfYesterday: result.budgetAsOfYesterday ?? 0,
        actualAsOfYesterday: result.actualAsOfYesterday ?? 0,
        billableRateSoFar: result.billableRateSoFar ?? 0,
        budgetBillableRate: result.budgetBillableRate ?? 0,
        expectedTotalHours: result.expectedTotalHours ?? 0,
        loggedTotalHours: result.loggedTotalHours ?? 0,
        absence: result.absence ?? 0,
        vacation: result.vacation ?? 0,
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch KPI data');
    } finally {
      setIsLoading(false);
    }
  }, [team, horizontalTeam, month, employee]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, filters.refresh || 60000);

  return { data, isLoading, error };
}

export function useTeamKPIsData(filters: EmbedFilters) {
  const [teamsData, setTeamsData] = useState<any[]>([]);
  const [employeesTable, setEmployeesTable] = useState<any[]>([]);
  const [resourcePlanningData, setResourcePlanningData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { team, horizontalTeam, month } = filters;
  const fetchData = useCallback(async () => {
    try {
      const qs = buildQueryParams({ team, horizontalTeam, month });
      const [teamsRes, empRes, rpRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/teams-data?${qs}`),
        fetch(`${getApiUrl()}/api/employees-table?${qs}`),
        fetch(`${getApiUrl()}/api/resource-planning?${qs}`),
      ]);
      if (!teamsRes.ok || !empRes.ok || !rpRes.ok) throw new Error('API error');
      const [teams, emps, rp] = await Promise.all([teamsRes.json(), empRes.json(), rpRes.json()]);
      setTeamsData(teams);
      setEmployeesTable(emps);
      setResourcePlanningData(rp);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch team KPI data');
    } finally {
      setIsLoading(false);
    }
  }, [team, horizontalTeam, month]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, filters.refresh || 60000);

  return { teamsData, employeesTable, resourcePlanningData, isLoading, error };
}

export function useTeamMembersData(filters: EmbedFilters) {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { team, horizontalTeam, month } = filters;
  const fetchData = useCallback(async () => {
    try {
      const qs = buildQueryParams({ team, horizontalTeam, month });
      const response = await fetch(`${getApiUrl()}/api/employees-table?${qs}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch team members data');
    } finally {
      setIsLoading(false);
    }
  }, [team, horizontalTeam, month]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, filters.refresh || 60000);

  return { data, isLoading, error };
}

export function useResourcePlanningData(filters: EmbedFilters) {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { team, horizontalTeam, employee } = filters;
  const fetchData = useCallback(async () => {
    try {
      const qs = buildQueryParams({ team, horizontalTeam, employee });
      const response = await fetch(`${getApiUrl()}/api/resource-planning?${qs}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch resource planning data');
    } finally {
      setIsLoading(false);
    }
  }, [team, horizontalTeam, employee]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, filters.refresh || 60000);

  return { data, isLoading, error };
}

export function useAnnouncementsData(filters: EmbedFilters) {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/announcements`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch announcements');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, filters.refresh || 60000);

  return { data, isLoading, error };
}
